/**
 * Sprint 233 — Creator Marketplace Packages (Client-facing)
 *
 * Premium grid of pre-built creator-content packages with fixed-price
 * Stripe Checkout for the standard tiers + Quote Request for Premium.
 *
 * Entry points:
 *  • /client/packages — this page
 *  • "Browse Packages" CTA on /client/influencers
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import {
  Sparkles, ArrowLeft, ArrowRight, Check, Loader2, ExternalLink,
  Clock, RefreshCw, ShieldCheck, Plus, Users, Star, Calendar, Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const BRAND = '#0DEAE3';

const ICON_BY_SLUG = {
  'ugc-creator':              { icon: Sparkles, accent: 'from-cyan-400/40 to-cyan-400/0' },
  'micro-influencer':         { icon: Users,    accent: 'from-emerald-400/40 to-cyan-400/0' },
  'influencer-campaign':      { icon: Star,     accent: 'from-violet-400/40 to-cyan-400/0' },
  'event-coverage':           { icon: Calendar, accent: 'from-amber-400/40 to-cyan-400/0' },
  'premium-custom':           { icon: Zap,      accent: 'from-fuchsia-500/50 to-cyan-400/0' },
  'event-staff-creator-addon': { icon: Plus,    accent: 'from-rose-400/40 to-cyan-400/0' },
};

const PackagesMarketplace = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/packages/catalog');
        if (!cancelled) setPackages(res.data || []);
      } catch {
        if (!cancelled) toast.error('Could not load packages');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F14] text-white" data-testid="packages-marketplace">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-white/5 bg-[#0B0F14]/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/client/dashboard')}
                  className="text-white/70 hover:text-white hover:bg-white/5" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full grid place-items-center"
                 style={{ background: `${BRAND}1A`, border: `1px solid ${BRAND}55` }}>
              <Sparkles className="w-4 h-4" style={{ color: BRAND }} />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-bold tracking-tight">Creator Packages</h1>
              <p className="text-[11px] text-white/40 leading-tight">UGC · Micro · Mid · Macro · Event Coverage</p>
            </div>
          </div>
          <div className="flex-1" />
          <Button variant="outline" size="sm"
                  onClick={() => navigate('/client/influencers')}
                  className="border-white/10 bg-white/5 text-white hover:bg-white/10"
                  data-testid="open-discovery-btn">
            Browse Creators <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 pb-3">
        <p className="text-[11px] uppercase tracking-widest text-[#0DEAE3] mb-2">Pre-built Creator Programs</p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Skip the brief. Buy the outcome.</h2>
        <p className="mt-2 text-white/60 max-w-2xl text-sm sm:text-base">
          Fixed-price creator packages with clear deliverables, turnaround, and usage rights.
          Pick a creator yourself or let us auto-match — checkout in minutes.
        </p>
      </div>

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="packages-grid">
            {packages.map(p => <PackageCard key={p.slug} pkg={p} onOpen={() => navigate(`/client/packages/${p.slug}`)} />)}
          </div>
        )}

        {/* Trust strip */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <Trust icon={ShieldCheck} text="Vetted creators only" />
          <Trust icon={Clock}       text="Clear turnaround timelines" />
          <Trust icon={RefreshCw}   text="Revisions included" />
          <Trust icon={Check}       text="Usage rights upfront" />
        </div>
      </main>
    </div>
  );
};

const PackageCard = ({ pkg, onOpen }) => {
  const Spec = ICON_BY_SLUG[pkg.slug] || { icon: Sparkles, accent: 'from-cyan-400/30 to-transparent' };
  const Icon = Spec.icon;
  const showStartsAt = pkg.starts_at_only;
  const priceLabel = pkg.is_custom_quote
    ? 'Custom Quote'
    : `${showStartsAt ? 'Starts at ' : ''}${pkg.price_display}`;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative text-left group rounded-2xl bg-white/[0.04] border border-white/[0.08] overflow-hidden transition-all hover:border-[#0DEAE3]/40 hover:shadow-[0_8px_40px_-8px_rgba(13,234,227,0.35)] hover:-translate-y-0.5 p-5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0DEAE3]/40"
      data-testid={`package-card-${pkg.slug}`}
    >
      <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full bg-gradient-to-br ${Spec.accent} blur-2xl opacity-60 pointer-events-none`} />

      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-10 h-10 rounded-xl grid place-items-center bg-[#0DEAE3]/10 border border-[#0DEAE3]/30">
          <Icon className="w-5 h-5 text-[#0DEAE3]" />
        </div>
        {pkg.is_custom_quote && (
          <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30">Bespoke</span>
        )}
      </div>

      <h3 className="text-lg font-bold text-white" data-testid={`package-name-${pkg.slug}`}>{pkg.name}</h3>
      {pkg.tagline && <p className="text-sm text-white/55 mt-0.5">{pkg.tagline}</p>}

      {/* Price */}
      <p className="mt-4 text-3xl font-bold text-white" data-testid={`package-price-${pkg.slug}`}>
        {pkg.is_custom_quote ? <span className="text-[#0DEAE3]">{priceLabel}</span> : (
          <>
            {showStartsAt && <span className="text-sm text-white/40 font-medium mr-1">From</span>}
            <span className="text-white">{pkg.price_display}</span>
          </>
        )}
      </p>

      {/* Highlights */}
      {(pkg.highlights || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {pkg.highlights.map(h => (
            <span key={h} className="px-2 py-0.5 rounded-full text-[10.5px] bg-white/5 border border-white/10 text-white/70">
              {h}
            </span>
          ))}
        </div>
      )}

      {/* Deliverables (first 3) */}
      <ul className="mt-4 space-y-1.5">
        {(pkg.deliverables || []).slice(0, 4).map(d => (
          <li key={d} className="flex items-start gap-2 text-[13px] text-white/75">
            <Check className="w-3.5 h-3.5 text-[#0DEAE3] mt-0.5 flex-shrink-0" /> {d}
          </li>
        ))}
        {(pkg.deliverables || []).length > 4 && (
          <li className="text-[12px] text-white/40 pl-5">+ {pkg.deliverables.length - 4} more</li>
        )}
      </ul>

      {/* Metadata strip */}
      <div className="mt-5 pt-3 border-t border-white/5 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider text-white/40">
        <Meta label="Turnaround" value={pkg.turnaround_days ? `${pkg.turnaround_days}d` : 'Custom'} />
        <Meta label="Revisions"  value={`${pkg.revisions}x`} />
        <Meta label="Tier"       value={pkg.estimated_tier?.toUpperCase() || '—'} />
      </div>

      {/* CTA hint */}
      <div className="mt-4 flex items-center justify-end text-[#0DEAE3] text-sm font-semibold opacity-80 group-hover:opacity-100">
        {pkg.is_custom_quote ? 'Request quote' : 'View & buy'} <ArrowRight className="w-3.5 h-3.5 ml-1" />
      </div>
    </button>
  );
};

const Meta = ({ label, value }) => (
  <div>
    <p className="text-[9px]">{label}</p>
    <p className="text-white/90 text-[12px] font-bold normal-case">{value}</p>
  </div>
);

const Trust = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 bg-white/[0.03] border border-white/5 rounded-lg p-3 text-white/60">
    <Icon className="w-4 h-4 text-[#0DEAE3] flex-shrink-0" />
    <span>{text}</span>
  </div>
);

const CardSkeleton = () => (
  <div className="rounded-2xl bg-white/[0.03] border border-white/5 p-5 animate-pulse">
    <div className="h-10 w-10 rounded-xl bg-white/[0.06] mb-3" />
    <div className="h-4 w-2/3 bg-white/[0.06] rounded mb-2" />
    <div className="h-3 w-1/2 bg-white/[0.06] rounded mb-4" />
    <div className="h-8 w-1/3 bg-white/[0.06] rounded mb-4" />
    <div className="space-y-2">
      <div className="h-3 w-full bg-white/[0.06] rounded" />
      <div className="h-3 w-5/6 bg-white/[0.06] rounded" />
      <div className="h-3 w-4/6 bg-white/[0.06] rounded" />
    </div>
  </div>
);

export default PackagesMarketplace;
